import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Swal from 'sweetalert2';
import config from '../../Config';

export default function EditProductForm({ productData, onCancel, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subCategory: '',
    keyFeatures: [''],
    isActive: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (productData) {
      setFormData({
        name: productData.name || '',
        description: productData.description || '',
        category: productData.category?._id || '',
        subCategory: productData.subCategory?._id || '',
        keyFeatures: productData.keyFeatures?.length ? productData.keyFeatures : [''],
        isActive: productData.isActive || false,
      });

      setPreview(productData.image || '');
    }
  }, [productData]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith('blob:')) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire('Error', 'Image must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);

    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    setPreview(URL.createObjectURL(file));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleKeyFeatureChange = (index, value) => {
    const updated = [...formData.keyFeatures];
    updated[index] = value;
    setFormData((prev) => ({ ...prev, keyFeatures: updated }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      keyFeatures: [...prev.keyFeatures, ''],
    }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => {
      const updated = prev.keyFeatures.filter((_, i) => i !== index);
      return {
        ...prev,
        keyFeatures: updated.length ? updated : [''],
      };
    });
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    const cleanedFeatures = formData.keyFeatures.map((f) => f.trim()).filter(Boolean);

    const formDataPayload = new FormData();
    formDataPayload.append('name', formData.name);
    formDataPayload.append('description', formData.description);
    formDataPayload.append('category', formData.category);
    formDataPayload.append('subCategory', formData.subCategory);
    formDataPayload.append('isActive', formData.isActive);
    formDataPayload.append('keyFeatures', JSON.stringify(cleanedFeatures));

    if (imageFile) {
      formDataPayload.append('image', imageFile);
    }

    try {
      const token = localStorage.getItem('token');

      const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product/edit/${productData._id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formDataPayload,
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire('Success', 'Product updated successfully.', 'success');
        onSuccess();
      } else {
        Swal.fire('Error', data.message || 'Failed to update product.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Something went wrong.', 'error');
    }

    setIsSubmitting(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', p: 3 }}>
      <Stack direction="row" alignItems="center" mb={3}>
        <IconButton onClick={onCancel}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" ml={1}>
          Edit Product
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        {/* LEFT */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Product Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            margin="normal"
            multiline
            rows={4}
          />

          {/* IMAGE UPLOAD */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              mt: 2,
              border: '2px dashed #ccc',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: '#fafafa',
            }}
          >
            <input
              type="file"
              hidden
              id="image-upload-edit"
              accept="image/*"
              onChange={(e) => handleFileChange(e.target.files[0])}
            />

            {!preview ? (
              <label htmlFor="image-upload-edit">
                <Typography>
                  Drag & Drop or <b>Click to Upload</b>
                </Typography>
              </label>
            ) : (
              <>
                <img src={preview} alt="preview" style={{ maxHeight: 150, objectFit: 'contain' }} />

                <Stack direction="row" spacing={1} justifyContent="center" mt={1}>
                  <Button component="label">
                    Replace
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                    />
                  </Button>

                  <Button
                    color="error"
                    onClick={() => {
                      if (preview.startsWith('blob:')) {
                        URL.revokeObjectURL(preview);
                      }
                      setImageFile(null);
                      setPreview('');
                    }}
                  >
                    Remove
                  </Button>
                </Stack>
              </>
            )}
          </Box>

          <Box
            sx={{
              mt: 3,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              Product Status
            </Typography>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox checked={formData.isActive} onChange={handleChange} name="isActive" />
              }
              label="Active"
            />
          </Box>
        </Grid>

        {/* RIGHT */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" mb={1}>
            Key Features (Optional)
          </Typography>

          <Stack spacing={1}>
            {formData.keyFeatures.map((feature, idx) => (
              <Stack direction="row" spacing={1} key={idx}>
                <TextField
                  fullWidth
                  label={`Feature ${idx + 1}`}
                  value={feature}
                  onChange={(e) => handleKeyFeatureChange(idx, e.target.value)}
                />
                <IconButton
                  onClick={() => removeFeature(idx)}
                  disabled={formData.keyFeatures.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>

          <Button startIcon={<AddIcon />} onClick={addFeature} sx={{ mt: 2 }}>
            Add Feature
          </Button>
        </Grid>
      </Grid>

      {/* ACTIONS */}
      <Stack direction="row" spacing={2} mt={4} justifyContent="flex-end">
        <Button variant="outlined" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>

        <Button variant="contained" type="submit" disabled={isSubmitting}>
          {isSubmitting ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
}
